/**
 * 初期流入処理（SPA） サービス
 */
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SupportClass } from '../../components/support-class';
import { uniqueId } from '../../helpers';
import {
  AnaBizLoginStatusType,
  AswServiceModel,
  BookingType,
  BrowserType,
  DeviceType,
  ErrorType,
  GenderCodeType,
  LoginStatusType,
  MemberKindCodeType,
  MobileDeviceType,
  NotRetryableErrorModel,
  PremiumStatusCodeType,
  SessionStorageName,
  TierLevelType,
} from '../../interfaces';
import { InitialInfluxRequest } from '../../model';
import {
  InitializationApiService,
  InitializationResponse,
  InitializationResponseDataAnaBizContext,
  InitializationResponseDataAswContext,
  InitializationResponseDataConfidential,
  InitializationResponseDataMemberInfo,
} from 'src/sdk-initialization';
import { CommonLibService } from '../common-lib/common-lib.service';
import { Router } from '@angular/router';
import { RoutesCommon } from '@conf/routes.config';
import { AnaCardTypeCodeType } from '@lib/interfaces/ana-card-type-code';
import { DatePipe } from '@angular/common';
import { I18N_CONFIG } from '@conf/i18n.config';
import { TranslateService } from '@ngx-translate/core';

/**
 * 初期流入処理 サービス
 */
@Injectable()
export class InitialInfluxService extends SupportClass {
  constructor(
    private _common: CommonLibService,
    private _initializationService: InitializationApiService,
    private _router: Router,
    private _translateSvc: TranslateService,
    private _datePipe: DatePipe
  ) {
    super();
  }

  destroy(): void {}

  public async exe() {
    if (!this.isCookieEnable()) {
      const errorInfo: NotRetryableErrorModel = {
        errorType: ErrorType.SYSTEM,
        errorMsgId: 'E0435',
        apiErrorCode: 'ECLC000001',
        isPopupPage: false,
      };
      this._common.errorsHandlerService.setNotRetryableError(errorInfo);

      // 共通エラー画面へ遷移
      this._router.navigateByUrl(RoutesCommon.SERVICE_ERROR);
      return;
    }

    // UUIDがなければ作成しセッションストレージに保存
    this.createIdentificationId();
    const requestParameters = this.getRequestParameter();

    // 面遷移元からパラメータとして受け取ったLANG
    let lang = requestParameters.lang;
    if (lang) {
      lang = lang in I18N_CONFIG.supportedLocales ? lang : 'en';
    } else {
      lang = I18N_CONFIG.defaultLanguage;
    }
    // 利用言語の設定
    this._translateSvc.use(lang);

    // companyReal CookieおよびlocalStorageのaccessTokenの有無に応じ、accessTokenを調整
    this._adjustAccessToken();

    this._common.apiErrorResponseService.clearApiErrorResponse(); //APIエラー情報を事前にクリア　初期流入でも一応しておく
    await new Promise<void>((resolve) => {
      //　初期流入API呼び出し、受信後処理
      this.subscribeService(
        'InitialInfluxServiceApiCall',
        this._initializationService.initializationPost(requestParameters, 'response'),
        (response: HttpResponse<InitializationResponse>) => {
          //正常終了
          if (requestParameters.jSessionId) {
            this._common.saveSessionStorage(requestParameters.jSessionId, SessionStorageName.JSESSION_ID);
          }
          if (response.body) {
            this._common.saveSessionStorage(response.body.accessToken, SessionStorageName.ACCESS_TOKEN);
          }
          const xCorrelationId = response.headers.get('X-Correlation-ID');
          if (xCorrelationId) {
            this._common.saveSessionStorage(xCorrelationId, SessionStorageName.X_CORRELATION_ID);
          }
          if (response.body?.data?.aswContext) {
            this.setAswContextStore(response.body.data.aswContext);
          }
          // ログインステータスがNOT_LOGIN以外の場合、storeに保存する
          if (response?.body?.data?.aswContext?.loginStatus !== LoginStatusType.NOT_LOGIN) {
            if (response.body?.data?.memberInfo) {
              this.setAmcMemberStore(response.body.data.memberInfo);
            }
          }
          // ANA BizログインステータスがNOT_LOGIN以外の場合、storeに保存する
          if (response.body?.data?.aswContext?.anaBizLoginStatus !== AnaBizLoginStatusType.NOT_LOGIN) {
            if (response.body?.data?.anaBizContext) {
              this.setAnaBizStore(response.body.data.anaBizContext);
            }
          }
          // confidentialをstoreに保存する
          if (response.body?.data?.confidential) {
            this.setConfidentialStore(response.body.data.confidential);
          }
          this.setAswServiceStore();
          resolve();
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            const errorCodeMap: { [key: string]: string } = {
              EAPZ000050: 'E0876', // Cookie情報(companyReal)とアクセストークンとの状態不正
              EAPZ000051: 'E0876', // Cookie情報、アクセストークン、DynamoDBとの状態不正
            };
            const apiCode = this._common.apiError?.errors?.[0]?.code ?? '';
            const code = errorCodeMap[apiCode];

            if (code) {
              const errorInfo: NotRetryableErrorModel = {
                errorType: ErrorType.BUSINESS_LOGIC,
                errorMsgId: code,
                apiErrorCode: apiCode,
                isPopupPage: false,
              };
              this._common.errorsHandlerService.setNotRetryableError(errorInfo);
            } else {
              // システムエラーへ
              this._common.errorsHandlerService.setNotRetryableError({
                errorType: ErrorType.SYSTEM, // システムロジックエラー
                apiErrorCode: apiCode, // APIエラーレスポンス情報
              });
            }
          }
          resolve();
        }
      );
    });
  }

  /** window object */
  private _window = window as any;

  /**
   * ASWユーザIDがなければ発行
   *
   */
  private createIdentificationId() {
    const identificationId = this._common.loadSessionStorage(SessionStorageName.IDENTIFICATION_ID);
    if (!identificationId) {
      this._common.saveSessionStorage(uniqueId(), SessionStorageName.IDENTIFICATION_ID);
    }
  }

  /**
   * 初期流入へ送るリクエストパラメータを作成
   */
  private getRequestParameter(): InitialInfluxRequest {
    // グローバル変数からqueryデータを取り出し
    const dataQuery = this._window.Asw.ApiRequestParam?.query ?? '{}';
    const requestQuery = this.parseJson(dataQuery);
    // グローバル変数からpostデータを取り出し
    const dataPost = this._window.Asw.ApiRequestParam?.post ?? '{}';
    const requestPost = this.parseJson(dataPost);
    const initialInfluxRequest: InitialInfluxRequest = {
      ...requestQuery,
      ...requestPost,
      userAgent: navigator.userAgent, // ユーザーエージェント情報
    };
    return initialInfluxRequest;
  }

  /**
   * 初期流入へ送るリクエストパラメータを作成
   */
  private parseJson(targetData: any): any {
    if (Object.keys(targetData).length !== 0) {
      const parsedData: any = {};
      if (targetData.hasOwnProperty('CONNECTION_KIND')) {
        parsedData.connectionKind = targetData.CONNECTION_KIND;
      }
      if (targetData.hasOwnProperty('LANG')) {
        parsedData.lang = targetData.LANG;
      }
      if (targetData.hasOwnProperty('TIMESTAMP')) {
        parsedData.timestamp = targetData.TIMESTAMP;
      }
      if (targetData.hasOwnProperty('apfCode')) {
        parsedData.apfCode = targetData.apfCode;
      }
      if (targetData.hasOwnProperty('jSessionId')) {
        parsedData.jSessionId = targetData.jSessionId;
      }
      if (targetData.hasOwnProperty('referer')) {
        parsedData.referer = targetData.referer;
      }
      return parsedData;
    } else {
      return {};
    }
  }

  /**
   * ユーザ共通情報をstoreに格納
   *
   */
  private setAswContextStore(aswContext: InitializationResponseDataAswContext) {
    this._common.aswContextStoreService.setAswContext({
      ...aswContext,
      bookingType: aswContext.bookingType as BookingType,
      deviceType: aswContext.deviceType as DeviceType,
      browserType: aswContext.browserType as BrowserType,
      mobileDeviceType: aswContext.mobileDeviceType as MobileDeviceType,
      loginStatus: aswContext.loginStatus as LoginStatusType,
      isViaGoshokaiNet: aswContext.isViaGoshokaiNet ?? false,
      isAnaApl: aswContext.isAnaApl ?? false,
      anaBizLoginStatus: aswContext.anaBizLoginStatus as AnaBizLoginStatusType,
      isDummyOffice: aswContext.isDummyOffice ?? false,
    });
  }

  /**
   * 会員情報をstoreに格納
   *
   */
  private setAmcMemberStore(amcMember: InitializationResponseDataMemberInfo) {
    this._common.amcMemberStoreService.setAMCMember({
      ...amcMember,
      memberKindCode: amcMember.memberKindCode as MemberKindCodeType,
      gender: amcMember.gender as GenderCodeType,
      premiumStatus: amcMember.premiumStatus as PremiumStatusCodeType,
      tierLevel: amcMember.tierLevel as TierLevelType,
      anaCardTypeCode: amcMember.anaCardTypeCode as AnaCardTypeCodeType,
    });
  }

  /**
   * ANA Bizログイン情報をstoreに格納
   *
   */
  private setAnaBizStore(anaBizContext: InitializationResponseDataAnaBizContext) {
    this._common.anaBizContextStoreService.setAnaBizContext({
      ...anaBizContext,
    });
  }

  /**
   * ANA Bizログイン情報(Confidential)をstoreに格納
   */
  private setConfidentialStore(confidential: InitializationResponseDataConfidential) {
    this._common.confidentialStoreService.setConfidential({
      ...confidential,
    });
  }

  /**
   * サービス共通情報をstoreに格納
   */
  private setAswServiceStore() {
    // セッションストレージを参照してデータが存在する場合
    const data = this._common.loadSessionStorage(SessionStorageName.ASW_SERVICE, true);
    const aswService: AswServiceModel = {
      orderId: data?.orderId ?? '',
      travelDocumentId: data?.travelDocumentId ?? '',
      lastName: data?.lastName ?? '',
      firstName: data?.firstName ?? '',
    };

    if (aswService.orderId || aswService.travelDocumentId || aswService.firstName || aswService.lastName) {
      this._common.aswServiceStoreService.setAswService(aswService);
    }
  }

  /**
   * accessTokenを調整
   */
  private _adjustAccessToken() {
    // localStorageのaccessTokenをsessionStorageに格納
    const accessTokenLS = localStorage.getItem(SessionStorageName.ACCESS_TOKEN) ?? '';
    const isCompanyReal = this._isCompanyReal();

    if (accessTokenLS) {
      // accessTokenが存在する場合
      if (isCompanyReal) {
        // companyRealが存在する場合、accessTokenをsessionStorageに保存
        this._common.saveSessionStorage(accessTokenLS, SessionStorageName.ACCESS_TOKEN);
      } else {
        // companyRealが存在しない場合、localStorageのaccessTokenを削除
        localStorage.removeItem(SessionStorageName.ACCESS_TOKEN);
      }
    }
  }

  /**
   * cookie有効判定
   */
  private isCookieEnable() {
    const nowDateStr = this._datePipe.transform(new Date(), 'yyyy-MM-ddTHH:mm:ssZ');
    const testName = 'test-cookie-name';
    const testValue = uniqueId() + nowDateStr; //前回値が残っているパターンもあるので毎回違う値にする
    //cookie書き込み
    document.cookie = testName + '=' + testValue + ';';
    //cookie読み込み
    const l = testName.length + 1;
    const cookieAry = document.cookie.split('; ');
    let str = '';
    for (const cookie of cookieAry) {
      if (cookie.substring(0, l) === testName + '=') {
        str = cookie.substring(l, cookie.length);
        document.cookie = testName + '=; max-age=0'; // テスト用に書き込んだcookieを削除
        break;
      }
    }

    if (str === testValue) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * companyRealCookieの存在チェック
   * @returns true:companyRealCookie有
   */
  private _isCompanyReal(): boolean {
    const cookieMatcher = new RegExp('companyReal' + '=([^;]*)');
    return !!cookieMatcher.exec(document.cookie ?? '')?.[1];
  }
}
